import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button, Spacer, Text, Select, TextField, Tooltip, BannerAlert } from '@truepill/react-capsule'
import LoadingSpinner from 'components/Loading'
import { CapsulePageContainer } from 'components/PageStructure'
import { CREATE_RULE_SET } from 'gql'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import { Info } from 'react-feather'
import { useHistory } from 'react-router'
import { goToAdminPage } from 'routes'
import styled from 'styled-components'
import type { TPOSLocation } from 'types'
import RuleCard from './components/RuleCard'

type ConditionType = 'SINGLE_VALUE' | 'MULTIPLE_VALUE'
type ValuesType = 'string' | 'array'
type OperatorType = 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'in' | 'not_in'
type ArrayMethod = 'all' | 'some'

export interface Condition {
  type: ConditionType | null
  field: string
  operator: OperatorType | null
  value?: string
  values?: string[]
  array_method?: ArrayMethod | null
  values_type?: ValuesType | null
}

export interface Rule {
  name: string
  description: string
  conditions: Condition[]
}

interface RuleSet {
  location: { id: number; name: string } | null
  rank: number
  name: string
  description?: string
  rules: Rule[]
}

const createDefaultCondition = (): Condition => ({
  type: null,
  field: '',
  operator: null,
  value: '',
  values_type: null,
})

const createDefaultRule = (): Rule => ({
  name: '',
  description: '',
  conditions: [createDefaultCondition()],
})

export const getAdornment =
  (label: string): (() => JSX.Element) =>
  () =>
    (
      <Tooltip label={label} position="top">
        <Info size={18} style={{ cursor: 'pointer', color: '#888' }} />
      </Tooltip>
    )

const CreateRuleSetPage: React.FC = () => {
  const history = useHistory()
  const { getLocations } = useTPCacheContext()
  const [ruleSet, setRuleSet] = useState<RuleSet>({
    location: null,
    rank: 0,
    name: '',
    description: '',
    rules: [createDefaultRule()],
  })
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const showSuccessToast = useSuccessToast()
  const [createRuleSet, { loading, error }] = useMutation(CREATE_RULE_SET, {
    onCompleted: () => {
      showSuccessToast('Routing Rule created successfully.')
      history.push(goToAdminPage())
    },
  })

  const options = useMemo<{ value: number; label: string }[]>(() => {
    return getLocations()
      .filter(l => l.active)
      .map((location: TPOSLocation) => ({
        value: location.legacyId,
        label: location.name,
      }))
  }, [getLocations])

  if (loading) {
    return (
      <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>
    )
  }

  const handleRuleSetChange = (field: keyof RuleSet, value: any) => {
    setRuleSet(prev => ({ ...prev, [field]: value }))
  }

  const handleRuleChange = (ruleIdx: number, field: keyof Rule, value: any) => {
    setRuleSet(prev => {
      const rules = [...prev.rules]
      rules[ruleIdx] = { ...rules[ruleIdx], [field]: value }
      return { ...prev, rules }
    })
  }

  const handleConditionChange = (ruleIdx: number, condIdx: number, field: keyof Condition, value: any) => {
    setRuleSet(prev => {
      const rules = [...prev.rules]
      const conditions = [...rules[ruleIdx].conditions]
      conditions[condIdx] = { ...conditions[condIdx], [field]: value }
      if (field === 'type' && value === 'SINGLE_VALUE') {
        conditions[condIdx].values = []
        conditions[condIdx].array_method = undefined
        conditions[condIdx].values_type = 'string'
      }
      if (field === 'type' && value === 'MULTIPLE_VALUE') {
        conditions[condIdx].value = ''
      }
      if (field === 'values_type' && value === 'string') {
        conditions[condIdx].operator = 'equals'
      }
      rules[ruleIdx].conditions = conditions
      return { ...prev, rules }
    })
  }

  const addRule = () => {
    setRuleSet(prev => ({
      ...prev,
      rules: [...prev.rules, createDefaultRule()],
    }))
  }

  const addCondition = (ruleIdx: number) => {
    setRuleSet(prev => {
      const rules = [...prev.rules]
      rules[ruleIdx] = {
        ...rules[ruleIdx],
        conditions: [...rules[ruleIdx].conditions, createDefaultCondition()],
      }
      return { ...prev, rules }
    })
  }

  const removeRule = (ruleIdx: number) => {
    setRuleSet(prev => ({
      ...prev,
      rules: prev.rules.filter((_, idx) => idx !== ruleIdx),
    }))
  }

  const removeCondition = (ruleIdx: number, condIdx: number) => {
    setRuleSet(prev => {
      const rules = [...prev.rules]
      rules[ruleIdx].conditions = rules[ruleIdx].conditions.filter((_, idx) => idx !== condIdx)
      return { ...prev, rules }
    })
  }

  const isFormValid = () => {
    if (!ruleSet.location || !ruleSet.name) return false

    for (const rule of ruleSet.rules) {
      if (!rule.name || !rule.description) return false
      for (const cond of rule.conditions) {
        if (!cond.type || !cond.field || !cond.operator) return false
        if (cond.type === 'SINGLE_VALUE' && !cond.value) return false
        if (cond.type === 'MULTIPLE_VALUE') {
          if (!cond.values_type) return false
          if (cond.values_type === 'string' && !cond.value) return false
          if (cond.values_type === 'array' && (!cond.values || cond.values.length === 0)) return false
        }
      }
    }
    return true
  }

  return (
    <CapsulePageContainer>
      {error && (
        <BannerAlert state="error">
          <Text variant="body-sm">{error.message}</Text>
        </BannerAlert>
      )}
      <Container>
        <Text as="h1">Create Routing Rule</Text>
        <Spacer size="md" />
        <form
          onSubmit={e => {
            e.preventDefault()
            setAttemptedSubmit(true)
            if (isFormValid()) {
              createRuleSet({
                variables: {
                  ruleSet,
                },
              })
            }
          }}
        >
          <Select
            label="Location"
            value={ruleSet.location ? options.find(l => l.value === ruleSet.location?.id) : null}
            onChange={selected => {
              const loc = options.find(l => l.value === selected?.value)
              handleRuleSetChange('location', { id: loc?.value, name: loc?.label })
            }}
            options={options}
            selectedKey="label"
            state={attemptedSubmit && !ruleSet.location ? 'error' : 'default'}
            helperText={attemptedSubmit && !ruleSet.location ? 'Location is required' : undefined}
          />
          <Spacer size="md" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TextField
              label="Rank"
              type="number"
              value={ruleSet.rank}
              onChange={e => handleRuleSetChange('rank', Number(e.target.value))}
              endAdornment={getAdornment(
                'Enter the rank for this rule. Higher-ranked rules are prioritized over others. By default, all rules have a rank of 0, so the first matching rule will be used.',
              )}
            />
          </div>
          <Spacer size="md" />
          <TextField
            label="Name"
            value={ruleSet.name}
            onChange={e => handleRuleSetChange('name', e.target.value)}
            state={attemptedSubmit && !ruleSet.name ? 'error' : 'default'}
            helperText={attemptedSubmit && !ruleSet.name ? 'Name is required' : undefined}
          />
          <Spacer size="md" />
          <TextField
            label="Description (optional)"
            value={ruleSet.description}
            onChange={e => handleRuleSetChange('description', e.target.value)}
          />
          <Spacer size="md" />
          <Text as="h3">Rules</Text>
          <Text as="p" variant="caption" style={{ marginBottom: 24 }}>
            You can add as many rules as you want, that would make an order route to the selected location. Each of them
            would work independently
          </Text>
          {ruleSet.rules.map((rule, ruleIdx) => (
            <RuleCard
              index={ruleIdx}
              rule={rule}
              onRuleChange={(field, value) => handleRuleChange(ruleIdx, field, value)}
              onConditionChange={(condIdx, field, value) => handleConditionChange(ruleIdx, condIdx, field, value)}
              onAddCondition={() => addCondition(ruleIdx)}
              onRemoveCondition={condIdx => removeCondition(ruleIdx, condIdx)}
              attemptedSubmit={attemptedSubmit}
              onRemoveRule={ruleSet.rules.length > 1 ? () => removeRule(ruleIdx) : undefined}
            />
          ))}
          <Button size="sm" type="button" onClick={addRule} variant="primary-text">
            Add Rule
          </Button>
          <Spacer size="lg" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
            <Button size="md" type="button" variant="primary-text" onClick={() => history.goBack()}>
              Back
            </Button>
            <Button size="md" type="submit" variant="primary">
              Create
            </Button>
          </div>
        </form>
      </Container>
    </CapsulePageContainer>
  )
}

const Container = styled.div`
  max-width: 900px;
  border-radius: 12px;
  padding: 2rem 2.5rem;
`

const LoadingSpinnerContainer = styled.div`
  width: 10rem;
  margin: 0 auto;
`

export default CreateRuleSetPage
